import React, { useState, useEffect } from 'react';
import { GeneratedImage, User } from '@/types';
import { suzuriApiClient } from '@/services/suzuriApi';
import { getPublicGoodsList, GoodsManagementItem } from '@/services/api/goods-management';
import { fetchGoodsOptions } from '@/services/api/goods-options';
import { trackMerchandiseCreation as trackMerchandiseCreationAds } from '../../utils/googleAds';
import { trackMerchandiseCreation as trackMerchandiseCreationGA4 } from '../../utils/googleAnalytics';
import PrintfulPurchaseModal from './PrintfulPurchaseModal';
import ArtisanPurchaseModal from './ArtisanPurchaseModal';
import ProdigiPurchaseModal from './ProdigiPurchaseModal';
import ProdigiOrderModal from './ProdigiOrderModal';
import { GoodsOrderStatus, OrderDetail, userOrdersAPI } from '@/services/api/user-orders';
import { AISHA_API_BASE } from '../../constants';
import {
  XMarkIcon as CloseIcon,
  ArrowPathIcon,
  ShoppingBagIcon,
  CheckIcon,
  ExclamationTriangleIcon,
  SparklesIcon,
  StarIcon,
  ArrowLeftIcon,
  EyeIcon,
  TruckIcon,
  ClockIcon,
  CheckCircleIcon,
  CalendarDaysIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  MapPinIcon,
} from '../icons/HeroIcons';

interface ExistingProductData {
  selectedItemId: string;
  merchandiseResult: MerchandiseResult;
}

interface SuzuriMerchandiseModalProps {
  isOpen: boolean;
  onClose: () => void;
  image: GeneratedImage;
  currentUser?: User | null; // ユーザー情報を追加
  onGoodsCreated?: () => void; // グッズ作成成功時のコールバック
  initialStep?: 'select' | 'preview' | 'result'; // 初期ステップ（デフォルト: 'select'）
  existingProductData?: ExistingProductData; // 既存商品データ（履歴から開く場合）
  orderStatus?: GoodsOrderStatus; // 購入ステータス情報（Printful商品のみ）
}

interface MerchandiseResult {
  success: boolean;
  productUrl?: string;
  product_url?: string;  // バックエンドからの互換性
  productTitle?: string;
  product_title?: string;  // バックエンドからの互換性
  sampleImageUrl?: string;
  sample_image_url?: string;  // バックエンドからの互換性
  itemName?: string;
  item_name?: string;  // バックエンドからの互換性
  productId?: number;
  product_id?: number;  // バックエンドからの互換性
  materialId?: number;
  material_id?: number;  // バックエンドからの互換性
  error?: string;
}

interface ItemOption {
  id: string;
  goodsId?: number; // 数値のGoodsManagement id（Prodigi価格/注文の紐付け）
  name: string;
  displayName: string;
  icon: string;
  icon_url?: string | null; // SUZURIのアイコン画像URL
  sample_image_url?: string | null; // SUZURIのサンプル画像URLを追加
  basePrice: number;
  finalPrice: number;
  description: string;
  gradient: string;
  suzuriItemId: number;
  isPublic: boolean;
  displayOrder: number;
  descriptions: string[];
  availablePrintPlaces: string[];
  isMultiPrintable: boolean;
  itemType: string; // SUZURI APIで使用する商品タイプ
  supplier: string; // サプライヤー（'suzuri' | 'printful'）
  vendorItemId?: string; // Printful用のカタログID
  apiConfig: { // SUZURI API設定
    itemId?: number;
    exemplaryItemVariantId?: number;
    sub_materials?: Array<{
      printSide: string;
    }>;
    resizeMode?: string;
  };
}

export const SuzuriMerchandiseModal: React.FC<SuzuriMerchandiseModalProps> = ({
  isOpen,
  onClose,
  image,
  currentUser,
  onGoodsCreated,
  initialStep = 'select',
  existingProductData,
  orderStatus: initialOrderStatus,
}) => {
  const [goodsItems, setGoodsItems] = useState<ItemOption[]>([]);
  const [isLoadingGoods, setIsLoadingGoods] = useState(false);
  const [showArtisanPurchase, setShowArtisanPurchase] = useState(false);
  const [selectedArtisanProduct, setSelectedArtisanProduct] = useState<any>(null);
  const [expandedOrders, setExpandedOrders] = useState<Set<string>>(new Set());
  const [orderStatus, setOrderStatus] = useState<GoodsOrderStatus | undefined>(initialOrderStatus);
  const [step, setStep] = useState<'select' | 'preview' | 'result'>(initialStep);

  // 公開グッズ一覧を取得
  useEffect(() => {
    const loadPublicGoods = async () => {
      setIsLoadingGoods(true);
      try {
        // まず、この画像で作成可能なサプライヤーを取得
        console.log('🔍 fetchGoodsOptions呼び出し前 - imageId:', image.id);
        const goodsOptions = await fetchGoodsOptions(image.id);
        console.log('📦 fetchGoodsOptions結果:', goodsOptions);
        console.log('🎯 allowedSuppliers:', goodsOptions.allowedSuppliers);
        console.log('📦 画像のグッズオプション:', goodsOptions);
        
        // Artisan公開商品を取得
        let artisanItems: ItemOption[] = [];
        if (goodsOptions.allowedSuppliers?.includes('artisan')) {
          const { getPublicArtisanProducts } = await import('@/services/api/artisan-product');
          const artisanProducts = await getPublicArtisanProducts();
          artisanItems = artisanProducts.map(p => ({
            id: `artisan_${p.id}`,
            name: `artisan_${p.id}`,
            displayName: p.product_name,
            icon: '🔧',
            icon_url: p.sample_image_url || null,
            sample_image_url: p.sample_image_url || null,
            basePrice: p.product_price,
            finalPrice: p.product_price,
            description: p.description,
            gradient: 'from-orange-400 to-orange-600',
            suzuriItemId: 0,
            isPublic: true,
            displayOrder: 0,
            descriptions: [p.description].filter(Boolean) as string[],
            availablePrintPlaces: [] as string[],
            isMultiPrintable: false,
            itemType: 'artisan',
            supplier: 'artisan',
            vendorItemId: String(p.id),
            apiConfig: {},
          } as ItemOption));
        }

        const publicGoods = await getPublicGoodsList();
        const convertedItems: ItemOption[] = publicGoods.map(goods => {
          // アイテム種類に応じてアイコンとグラデーションを設定
          const getItemIcon = (itemName: string) => {
            const iconMap: { [key: string]: string } = {
              'dry-t-shirt': '👕',
              'smartphone-case': '📱',
              'android-smartphone-case': '📱',
              'big-shoulder-bag': '👜',
              'thermo-tumbler': '🥤',
              'sticker': '🏷️',
              'clear-file-folder': '📁',
            };
            return iconMap[itemName] || '📦';
          };

          const getItemGradient = (itemName: string) => {
            const gradientMap: { [key: string]: string } = {
              'dry-t-shirt': 'from-blue-400 to-blue-600',
              'smartphone-case': 'from-purple-400 to-purple-600',
              'android-smartphone-case': 'from-purple-400 to-purple-600',
              'big-shoulder-bag': 'from-green-400 to-green-600',
              'thermo-tumbler': 'from-orange-400 to-orange-600',
              'sticker': 'from-yellow-400 to-yellow-600',
              'clear-file-folder': 'from-indigo-400 to-indigo-600',
            };
            return gradientMap[itemName] || 'from-blue-400 to-blue-600';
          };

          return {
            id: goods.item_name,
            goodsId: goods.id,   // 数値のGoodsManagement id（Prodigi価格/注文の紐付けに必須）
            name: goods.item_name,
            displayName: goods.display_name,
            icon: getItemIcon(goods.item_name), // フォールバック用の絵文字アイコン
            icon_url: goods.icon_url, // SUZURIのアイコン画像URL
            sample_image_url: goods.sample_image_url, // SUZURIのサンプル画像URL
            basePrice: goods.base_price,
            finalPrice: goods.final_price,
            description: Array.isArray(goods.descriptions) ? goods.descriptions.join(', ') : '',
            gradient: getItemGradient(goods.item_name),
            suzuriItemId: goods.suzuri_item_id,
            isPublic: goods.is_public,
            displayOrder: goods.display_order,
            descriptions: Array.isArray(goods.descriptions) ? goods.descriptions : [],
            availablePrintPlaces: goods.available_print_places || [],
            isMultiPrintable: goods.is_multi_printable,
            itemType: goods.item_type || goods.item_name, // 管理画面のitem_typeを使用
            supplier: goods.supplier || 'suzuri', // サプライヤー情報を追加
            vendorItemId: goods.vendor_item_id, // Printful用のカタログID
            apiConfig: goods.api_config || { itemId: goods.suzuri_item_id, exemplaryItemVariantId: null, sub_materials: [], resizeMode: 'cover' }, // 管理画面のapi_configを使用
          };
        });
        
        // デバッグログ: 取得した商品一覧を確認
        console.log('🔄 公開商品一覧:', convertedItems.map(item => ({
          id: item.id,
          displayName: item.displayName,
          itemType: item.itemType,
          suzuriItemId: item.suzuriItemId,
          apiConfig: item.apiConfig,
          supplier: item.supplier
        })));
        
        // Artisan商品をマージ
        const allItems = [...convertedItems, ...artisanItems];

        // allowedSuppliersに基づいてフィルタリング
        console.log('🔍 フィルタリング開始');
        console.log('🔍 goodsOptions.allowedSuppliers:', goodsOptions.allowedSuppliers);

        const filteredItems = allItems.filter(item => {
          // allowedSuppliersが空の場合は何も表示しない
          if (!goodsOptions.allowedSuppliers || goodsOptions.allowedSuppliers.length === 0) {
            console.log(`⚠️ ${item.displayName}: allowedSuppliersが空なので非表示`);
            return false;
          }
          // 商品のサプライヤーが許可リストに含まれているかチェック
          const isAllowed = goodsOptions.allowedSuppliers.includes(item.supplier);
          console.log(`🔍 ${item.displayName} (supplier: ${item.supplier}): ${isAllowed ? '✅ 表示' : '❌ 非表示'}`);
          return isAllowed;
        });
        
        console.log(`🎯 フィルタリング後の商品数: ${filteredItems.length}/${convertedItems.length}`);
        console.log('✅ 許可されたサプライヤー:', goodsOptions.allowedSuppliers);
        
        setGoodsItems(filteredItems);
        setAllowedSuppliers(goodsOptions.allowedSuppliers || []);
      } catch (error) {
        console.error('公開グッズ一覧取得エラー:', error);
      } finally {
        setIsLoadingGoods(false);
      }
    };

    if (isOpen && step === 'select') {
      loadPublicGoods();
    }
  }, [isOpen, step]);

  const [selectedItem, setSelectedItem] = useState<ItemOption | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [result, setResult] = useState<MerchandiseResult | null>(existingProductData?.merchandiseResult || null);
  const [previewAnimation, setPreviewAnimation] = useState(false);
  const [showPrintfulModal, setShowPrintfulModal] = useState(false);
  const [selectedPrintfulItem, setSelectedPrintfulItem] = useState<ItemOption | null>(null);
  const [showProdigiModal, setShowProdigiModal] = useState(false);
  const [selectedProdigiItem, setSelectedProdigiItem] = useState<ItemOption | null>(null);
  const [showProdigiOrderModal, setShowProdigiOrderModal] = useState(false);
  const [prodigiOrderStatus, setProdigiOrderStatus] = useState<any>(null); // Prodigi購入済ステータス
  const [isPublished, setIsPublished] = useState<boolean>(true);  // 公開状態の管理
  const [isTogglingVisibility, setIsTogglingVisibility] = useState(false);  // 切り替え中の状態
  const [allowedSuppliers, setAllowedSuppliers] = useState<string[]>(['suzuri', 'printful']); // デフォルトは両方許可

  // 公開/非公開切り替え機能
  const toggleVisibility = async () => {
    if (!result?.merchandise_id) return;
    
    setIsTogglingVisibility(true);
    try {
      const response = await fetch(`${AISHA_API_BASE}/suzuri/toggle-visibility/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          merchandise_id: result.merchandise_id,
          is_published: !isPublished,
        }),
      });

      if (response.ok) {
        const data = await response.json();
        setIsPublished(data.is_published);
        console.log('✅ 公開状態を変更しました:', data.message);
      } else {
        console.error('❌ 公開状態の変更に失敗しました');
      }
    } catch (error) {
      console.error('❌ エラー:', error);
    } finally {
      setIsTogglingVisibility(false);
    }
  };

  // Printful購入完了時のコールバック
  const handlePrintfulPurchaseCompleted = async () => {
    console.log('🎉 Printful購入完了 - 注文ステータスを更新');
    
    // merchandiseIdがある場合は注文ステータスを再取得
    if (result?.merchandise_id && currentUser?.id) {
      try {
        const response = await userOrdersAPI.getGoodsOrderStatus(
          currentUser.id,
          String(result.merchandise_id)
        );
        
        if (response.success && response.order) {
          console.log('✅ 注文ステータス更新成功:', response.order);
          setOrderStatus(response.order);
          
          // スクロール位置を最上部にリセット
          // モーダル内のスクロール可能な要素を探して最上部に移動
          setTimeout(() => {
            const modalContent = document.querySelector('.max-h-\\[60vh\\]');
            if (modalContent) {
              modalContent.scrollTop = 0;
              console.log('📍 スクロール位置を最上部にリセット');
            }
          }, 100); // 少し遅延を入れて確実にDOMが更新された後に実行
        }
      } catch (error) {
        console.error('注文ステータス取得エラー:', error);
      }
    }
  };

  // 既存商品データがある場合は、goodsItemsがロードされたらselectedItemを設定
  useEffect(() => {
    if (existingProductData && goodsItems.length > 0 && !selectedItem) {
      const foundItem = goodsItems.find(item => item.id === existingProductData.selectedItemId);
      if (foundItem) {
        setSelectedItem(foundItem);
      }
    }
  }, [existingProductData, goodsItems, selectedItem]);

  // Prodigi購入済みアイテムの注文ステータスを取得（Printfulと同じ緑ボックス表示用）
  useEffect(() => {
    const mid = (result as any)?.merchandise_id || (result as any)?.merchandiseId;
    if (isOpen && result?.product_url === 'Prodigi' && mid) {
      fetch(`${AISHA_API_BASE}/prodigi/order-status/?merchandise_id=${mid}`)
        .then(r => r.json())
        .then(d => setProdigiOrderStatus(d?.has_order ? d : null))
        .catch(() => setProdigiOrderStatus(null));
    } else {
      setProdigiOrderStatus(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, (result as any)?.merchandise_id, result?.product_url]);

  // 購入済ステータス（Prodigiは自前fetch, それ以外はpropのorderStatus）
  const purchasedOrderStatus: any = (result as any)?.product_url === 'Prodigi' ? prodigiOrderStatus : orderStatus;
  // 今作成した直後(fresh)か、履歴から開いた(既存)か。履歴閲覧では「作成完了！」の演出を出さない。
  const isFreshCreation = !existingProductData;

  // デバッグ: previewステップの条件チェック
  const checkPreviewCondition = () => {
    const stepCheck = step === 'preview';
    const itemCheck = selectedItem !== null;
    const result = stepCheck && itemCheck;
    return result;
  };

  useEffect(() => {
    if (step === 'preview') {
      setPreviewAnimation(true);
      const timer = setTimeout(() => setPreviewAnimation(false), 300);
      return () => clearTimeout(timer);
    }
  }, [step]);

  if (!isOpen) return null;

  // 車の名前を抽出
  const extractCarName = (prompt: string): string => {
    if (!prompt) return 'AISHA生成画像';
    
    // 技術的なプレフィックスを除去
    let cleanPrompt = prompt
      .replace(/^背景拡張[:：]\s*/g, '')
      .replace(/^Please take photos of the car[^.]*\./g, '')
      .replace(/^画像拡張[:：]\s*/g, '')
      .replace(/^Extension[:：]\s*/g, '');
    
    // 車種名のキーワードを探す
    const carKeywords = [
      'NISSAN', 'TOYOTA', 'HONDA', 'MAZDA', 'SUBARU', 'MITSUBISHI',
      'BMW', 'MERCEDES', 'AUDI', 'VOLKSWAGEN', 'PORSCHE',
      'FERRARI', 'LAMBORGHINI', 'MASERATI',
      'FORD', 'CHEVROLET', 'DODGE', 'TESLA',
      'FAIRLADY', 'SUPRA', 'CIVIC', 'ACCORD', 'SKYLINE', 'GT-R'
    ];
    
    for (const keyword of carKeywords) {
      if (cleanPrompt.toUpperCase().includes(keyword)) {
        const index = cleanPrompt.toUpperCase().indexOf(keyword);
        const start = Math.max(0, index - 10);
        const end = Math.min(cleanPrompt.length, index + keyword.length + 20);
        return cleanPrompt.slice(start, end).trim();
      }
    }
    
    // 車種名が見つからない場合は、最初の50文字を使用
    return cleanPrompt.slice(0, 50).trim() || 'AISHA生成画像';
  };

  const _get_item_display_name = (item_name: string, requested_type: string) => {
    const display_names: Record<string, string> = {
      'dry-t-shirt': 'ドライTシャツ',
      'smartphone-case': 'iPhoneケース',
      'big-shoulder-bag': 'ショルダーバッグ',
      'thermo-tumbler': 'タンブラー',
      'sticker': 'ステッカー',
      'clear-file-folder': 'クリアファイル',
    };
    
    // requested_typeから優先的に表示名を取得
    if (requested_type in display_names) {
      return display_names[requested_type];
    }
    
    // item_nameから表示名を取得
    if (item_name in display_names) {
      return display_names[item_name];
    }
    
    // マッピングにない場合はそのまま返す
    return item_name || 'グッズ';
  };

  // アイテムタイプ別の価格を取得する関数
  const getItemPrice = (itemType: string): string => {
    const priceMapping: Record<string, string> = {
      'dry-t-shirt': '¥5,100〜',
      'smartphone-case': '¥3,000〜',
      'big-shoulder-bag': '¥5,000〜',
      'thermo-tumbler': '¥4,100〜',
      'sticker': '¥1,100〜',
      'clear-file-folder': '¥2,200〜',
    };
    
    return priceMapping[itemType] || '¥5,100〜';
  };

  const carName = extractCarName(image.displayPrompt);

  const handleItemSelect = (item: ItemOption) => {
    // デバッグログ: 選択された商品の詳細を確認
    console.log('🔄 商品選択:', {
      id: item.id,
      displayName: item.displayName,
      itemType: item.itemType,
      supplier: item.supplier,
      vendorItemId: item.vendorItemId,
      suzuriItemId: item.suzuriItemId,
      apiConfig: item.apiConfig
    });
    
    // Artisan商品の場合はArtisan購入モーダルを開く
    if (item.supplier === 'artisan' && item.vendorItemId) {
      import('@/services/api/artisan-product').then(({ getArtisanProduct }) => {
        getArtisanProduct(Number(item.vendorItemId)).then((product) => {
          setSelectedArtisanProduct(product);
          setShowArtisanPurchase(true);
        }).catch((err) => {
          console.error('Artisan商品取得エラー:', err);
        });
      });
      return;
    }

    // Prodigi商品は専用モーダル（位置調整エディタ）を開く
    if (item.supplier === 'prodigi') {
      setSelectedProdigiItem(item);
      setShowProdigiModal(true);
      return;
    }

    // Printful商品もSUZURI商品も同じくプレビューステップへ
    setSelectedItem(item);
    setStep('preview');
  };

  const handleCreate = async () => {
    if (!selectedItem) return;

    // ステップ3（result）に進む（SUZURI/Printful共通）
    setIsCreating(true);
    setStep('result');

    try {
      let response;
      
      if (selectedItem.supplier === 'printful' && selectedItem.vendorItemId) {
        // Printful商品の場合
        let variantId = null;
        let variantInfo = null;
        
        // カタログからバリアント情報を動的に取得
        try {
          console.log('📋 バリアント情報を取得中... catalog_id:', selectedItem.vendorItemId);
          const variantResponse = await fetch(`${AISHA_API_BASE}/printful/catalog/${selectedItem.vendorItemId}/variants/`);
          if (variantResponse.ok) {
            const variantData = await variantResponse.json();
            if (variantData.success && variantData.default_variant) {
              variantInfo = variantData.default_variant;
              variantId = variantInfo.variant_id;
              console.log('✅ バリアント取得成功:', variantInfo);
            }
          }
        } catch (error) {
          console.warn('⚠️ バリアント取得エラー（フォールバック使用）:', error);
        }
        
        // バリアントIDが取得できなかった場合はapi_configから取得
        if (!variantId) {
          variantId = selectedItem.apiConfig?.variant_id || selectedItem.apiConfig?.default_variant_id || null;
        }
        
        const requestData = {
          user_id: currentUser?.id,
          image_url: image.url,
          catalog_id: selectedItem.vendorItemId,
          variant_id: variantId, // 動的に取得したvariant_id（nullでもOK、バックエンドで処理）
          product_id: selectedItem.id, // GoodsManagementのIDを使用（数値）
          product_name: selectedItem.displayName,
          variant_name: variantInfo?.name || selectedItem.itemType,
          size: variantInfo?.size || selectedItem.apiConfig?.size || '',
          color: variantInfo?.color || selectedItem.apiConfig?.color || '',
          price: variantInfo?.price || selectedItem.finalPrice
        };
        
        console.log('🔄 Printful API リクエストデータ:', requestData);
        
        const apiResponse = await fetch(`${AISHA_API_BASE}/printful/create-merchandise/`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(requestData)
        });
        
        const data = await apiResponse.json();
        
        response = {
          success: data.success,
          error: data.error,
          product_url: 'Printful', 
          management_code: data.management_code,
          merchandise_id: data.merchandise_id,
          sample_image_url: data.mockup_url || image.url,
          message: data.message || 'Printful商品を作成しました'
        };
        
      } else {
        // SUZURI商品の場合
        const requestData = {
          image_url: image.url,
          car_name: carName,
          description: `${carName} ${selectedItem.displayName}`,
          item_type: selectedItem.itemType, // 管理画面のitem_typeを使用
          item_id: selectedItem.suzuriItemId,  // SUZURIアイテムIDを追加
          user_id: currentUser?.id,  // ユーザーIDを追加
          additional_profit: selectedItem.finalPrice - selectedItem.basePrice,  // 追加利益を追加
          print_places: selectedItem.availablePrintPlaces,  // プリント位置を追加
          is_multi_printable: selectedItem.isMultiPrintable,  // マルチプリント可能フラグを追加
          api_config: selectedItem.apiConfig, // 管理画面のAPI設定を追加
        };

        // デバッグログ: 送信データを確認
        console.log('🔄 SUZURI API リクエストデータ:', {
          item_type: requestData.item_type,
          item_id: requestData.item_id,
          api_config: requestData.api_config,
          selectedItem: {
            id: selectedItem.id,
            displayName: selectedItem.displayName,
            itemType: selectedItem.itemType,
            apiConfig: selectedItem.apiConfig
          }
        });

        response = await suzuriApiClient.createMerchandise(requestData);
      }
      setResult(response);
      
      // グッズ作成成功時の処理
      if (response.success) {
        // Google Ads コンバージョントラッキング
        trackMerchandiseCreationAds();
        
        // Google Analytics 4 イベントトラッキング
        const itemType = selectedItem?.itemType || '不明';
        const itemName = selectedItem?.displayName || '不明';
        trackMerchandiseCreationGA4(itemType, itemName);
        
        // コールバックを呼び出し
        if (onGoodsCreated) {
          onGoodsCreated();
        }
      }
    } catch (error: any) {
      console.error('SUZURI merchandise creation failed:', error);
      
      let errorMessage = 'グッズ作成中にエラーが発生しました';
      if (error.message.includes('API Error: 400')) {
        errorMessage = 'リクエストの形式が正しくありません。画像または車名に問題があります。';
      } else if (error.message.includes('API Error: 401')) {
        errorMessage = 'SUZURI APIの認証に失敗しました。管理者に問い合わせてください。';
      } else if (error instanceof Error) {
        errorMessage = `エラー: ${error.message}`;
      }
      
      setResult({
        success: false,
        error: errorMessage,
      });
    } finally {
      setIsCreating(false);
    }
  };

  const handleBack = () => {
    if (step === 'preview') {
      setStep('select');
      setSelectedItem(null);
    } else if (step === 'result') {
      setStep('select');
      setSelectedItem(null);
      setResult(null);
    }
  };

  const handleClose = () => {
    setStep('select');
    setSelectedItem(null);
    setResult(null);
    onClose();
  };

  // アコーディオンの開閉を制御
  const toggleOrderExpanded = (orderId: string) => {
    const newExpanded = new Set(expandedOrders);
    if (newExpanded.has(orderId)) {
      newExpanded.delete(orderId);
    } else {
      newExpanded.add(orderId);
    }
    setExpandedOrders(newExpanded);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-[60] p-4 backdrop-blur-sm">
      <div 
        className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden relative transform transition-all duration-300 scale-100"
        onClick={(e) => e.stopPropagation()}
      >
        {/* シンプルなヘッダー */}
        <div className="bg-gradient-to-r from-pink-500 to-purple-600 p-4 text-white relative">
          <button
            onClick={handleClose}
            className="absolute top-3 right-3 p-1.5 hover:bg-white hover:bg-opacity-20 rounded-full transition-all duration-200"
          >
            <CloseIcon className="w-5 h-5" />
          </button>
          
          <div className="pr-10">
            <div className="flex items-center space-x-2">
              <div className="bg-white bg-opacity-20 p-2 rounded-full">
                <ShoppingBagIcon className="w-6 h-6" />
              </div>
              <div>
                <h2 className="text-xl font-bold">
                  オリジナルアイテム作成
                </h2>
                <p className="text-pink-100 text-sm">
                  あなただけの特別なアイテムを作ろう
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* シンプルな進行状況インジケーター */}
        <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
          <div className="flex items-center justify-center space-x-4">
            <div className={`flex items-center space-x-2 ${
              step === 'select' ? 'text-purple-600' : 'text-gray-400'
            }`}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                step === 'select' 
                  ? 'bg-purple-600 text-white' 
                  : 'bg-gray-200 text-gray-600'
              }`}>
                1
              </div>
              <span className="text-sm font-medium">アイテム選択</span>
            </div>
            
            <div className="w-8 h-1 bg-gray-200 rounded-full">
              <div className={`h-full bg-purple-400 rounded-full transition-all duration-500 ${
                step !== 'select' ? 'w-full' : 'w-0'
              }`}></div>
            </div>
            
            <div className={`flex items-center space-x-2 ${
              step === 'preview' ? 'text-purple-600' : 'text-gray-400'
            }`}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                step === 'preview' 
                  ? 'bg-purple-600 text-white' 
                  : step === 'result' 
                    ? 'bg-green-500 text-white' 
                    : 'bg-gray-200 text-gray-600'
              }`}>
                {step === 'result' ? <CheckIcon className="w-4 h-4" /> : '2'}
              </div>
              <span className="text-sm font-medium">プレビュー</span>
            </div>
            
            <div className="w-8 h-1 bg-gray-200 rounded-full">
              <div className={`h-full bg-purple-400 rounded-full transition-all duration-500 ${
                step === 'result' ? 'w-full' : 'w-0'
              }`}></div>
            </div>
            
            <div className={`flex items-center space-x-2 ${
              step === 'result' ? 'text-purple-600' : 'text-gray-400'
            }`}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                step === 'result' 
                  ? 'bg-green-500 text-white' 
                  : 'bg-gray-200 text-gray-600'
              }`}>
                {step === 'result' ? <CheckIcon className="w-4 h-4" /> : '3'}
              </div>
              <span className="text-sm font-medium">完成</span>
            </div>
          </div>
        </div>

        {/* コンテンツエリア */}
        <div className="p-6 max-h-[60vh] overflow-y-auto">
          {step === 'select' && (
            <div className="space-y-8">
              {/* シンプルな画像プレビュー */}
              <div className="bg-gradient-to-br from-purple-50 to-pink-50 p-4 rounded-2xl border border-purple-100 shadow-sm">
                <div className="flex items-center justify-center">
                  <div className="relative">
                    <img
                      src={image.url}
                      alt="生成画像"
                      className="w-20 h-20 object-cover rounded-xl shadow-md border-2 border-white"
                    />
                  </div>
                </div>
              </div>

              {/* シンプルなセクションタイトル */}
              <div className="text-center">
                <h3 className="text-xl font-bold text-gray-800 mb-2 flex items-center justify-center">
                  <ShoppingBagIcon className="w-5 h-5 text-purple-500 mr-2" />
                  作成するアイテムを選択してください
                </h3>
              </div>

              {/* シンプルなグッズ選択グリッド */}
              {isLoadingGoods ? (
                <div className="flex justify-center items-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
                  <span className="ml-2 text-gray-600">アイテム一覧を読み込み中...</span>
                </div>
              ) : goodsItems.length > 0 ? (
                <div className="grid grid-cols-3 gap-4">
                  {goodsItems.map((item) => (
                    <button
                      key={item.id}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleItemSelect(item);
                      }}
                      className={`group p-4 border rounded-xl transition-all duration-200 text-center ${
                        item.supplier === 'printful'
                          ? 'bg-gray-900 border-gray-700 hover:border-purple-500 hover:shadow-lg hover:shadow-purple-500/20'
                          : 'bg-white border-gray-200 hover:border-purple-300 hover:shadow-md'
                      }`}
                    >
                      <div className={`w-12 h-12 bg-gradient-to-br ${item.gradient} rounded-lg flex items-center justify-center text-xl mb-2 mx-auto group-hover:scale-105 transition-transform duration-200`}>
                        {item.sample_image_url ? (
                          <img src={item.sample_image_url} alt={item.displayName} className="w-full h-full object-contain rounded-md" />
                        ) : item.icon_url ? (
                          <img src={item.icon_url} alt={item.displayName} className="w-full h-full object-contain rounded-md" />
                        ) : (
                          item.icon
                        )}
                      </div>
                      <h4 className={`font-semibold text-sm ${
                        item.supplier === 'printful' ? 'text-white' : 'text-gray-800'
                      }`}>
                        {item.displayName}
                      </h4>
                      <p className={`text-xs mt-1 ${
                        item.supplier === 'printful' ? 'text-gray-400' : 'text-gray-500'
                      }`}>
                        ¥{item.finalPrice.toLocaleString()}
                      </p>
                    </button>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <p className="text-gray-500">この画像から作れるアイテムがありません</p>
                </div>
              )}

              {/* シンプルな注意事項（許可されたサプライヤーのみ表示） */}
              <div className="flex gap-3">
                {/* SUZURIについて */}
                {allowedSuppliers.includes('suzuri') && (
                  <div className="flex-1 bg-blue-50 border border-blue-200 p-3 rounded-xl">
                    <div className="flex items-start space-x-2">
                      <div className="text-blue-500 mt-0.5">💡</div>
                      <div>
                        <p className="text-blue-800 font-semibold text-sm mb-1">SUZURIについて</p>
                        <p className="text-blue-700 text-xs leading-relaxed">
                          作成後、SUZURIのサイトで安全にお買い物ができます。
                        </p>
                      </div>
                    </div>
                  </div>
                )}
                
                {/* Printfulについて */}
                {allowedSuppliers.includes('printful') && (
                  <div className="flex-1 bg-gray-900 border border-gray-700 p-3 rounded-xl">
                    <div className="flex items-start space-x-2">
                      <div className="text-purple-400 mt-0.5">🛍️</div>
                      <div>
                        <p className="text-white font-semibold text-sm mb-1">Printfulについて</p>
                        <p className="text-gray-300 text-xs leading-relaxed">
                          作成後、Printfulのアイテムの購入ができます。
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {step === 'preview' && selectedItem && (
            <div className="space-y-6">
              {/* シンプルなアイテムヘッダー */}
              <div className="text-center">
                <div className={`w-16 h-16 bg-gradient-to-br ${selectedItem.gradient} rounded-2xl flex items-center justify-center text-2xl mx-auto mb-4`}>
                  {selectedItem.sample_image_url ? (
                    <img src={selectedItem.sample_image_url} alt={selectedItem.displayName} className="w-full h-full object-contain" />
                  ) : selectedItem.icon_url ? (
                    <img src={selectedItem.icon_url} alt={selectedItem.displayName} className="w-full h-full object-contain" />
                  ) : (
                    selectedItem.icon
                  )}
                </div>
                <h3 className="text-2xl font-bold text-gray-800">
                  {selectedItem.displayName}
                </h3>
              </div>

              {/* シンプルなプレビューカード */}
              <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-2xl p-6 border border-purple-200">
                <div className="grid md:grid-cols-2 gap-6 items-center">
                  {/* シンプルなプレビュー画像 */}
                  <div className="bg-white p-4 rounded-xl shadow-md">
                    <div className="w-full h-48 bg-gray-100 rounded-lg flex items-center justify-center relative overflow-hidden">
                      <img
                        src={image.url}
                        alt="車の画像"
                        className="w-32 h-24 object-contain rounded-md"
                      />
                      <div className="absolute bottom-2 right-2 bg-gradient-to-r from-purple-400 to-pink-400 text-white text-xs px-2 py-1 rounded-full font-bold">
                        {selectedItem.displayName}
                      </div>
                    </div>
                  </div>

                  {/* 商品詳細情報 */}
                  <div className="space-y-4">
                    <h4 className="text-lg font-bold text-gray-800 flex items-center">
                      <ShoppingBagIcon className="w-5 h-5 text-purple-500 mr-2" />
                      商品詳細
                    </h4>
                    <div className="space-y-3">
                      {/* ベンダー */}
                      <div className="bg-white rounded-xl p-4 shadow-sm">
                        <div className="flex items-center justify-between">
                          <span className="text-gray-600 text-sm font-medium">ベンダー</span>
                          <span className="font-bold text-gray-800">
                            {selectedItem.supplier === 'printful' ? 'Printful' : 'SUZURI'}
                          </span>
                        </div>
                      </div>
                      
                      {/* アイテムタイプ */}
                      <div className="bg-white rounded-xl p-4 shadow-sm">
                        <div className="flex items-center justify-between">
                          <span className="text-gray-600 text-sm font-medium">アイテム</span>
                          <span className="font-bold text-gray-800">
                            {selectedItem.displayName}
                          </span>
                        </div>
                      </div>
                      
                      {/* プリント位置 */}
                      <div className="bg-white rounded-xl p-4 shadow-sm">
                        <div className="flex items-center justify-between">
                          <span className="text-gray-600 text-sm font-medium">プリント位置</span>
                          <span className="font-medium text-gray-800">
                            {selectedItem.availablePrintPlaces.length > 0 
                              ? selectedItem.availablePrintPlaces.join(', ')
                              : 'フロント中央'
                            }
                          </span>
                        </div>
                      </div>
                      
                      {/* 印刷品質 */}
                      <div className="bg-white rounded-xl p-4 shadow-sm">
                        <div className="flex items-center justify-between">
                          <span className="text-gray-600 text-sm font-medium">印刷品質</span>
                          <span className="font-medium text-gray-800">
                            高品質プリント
                          </span>
                        </div>
                      </div>
                      
                      {/* 価格 */}
                      <div className="bg-white rounded-xl p-4 shadow-sm">
                        <div className="flex items-center justify-between">
                          <span className="text-gray-600 text-sm font-medium">販売価格</span>
                          <span className="font-bold text-purple-600 text-xl">
                            ¥{selectedItem.finalPrice.toLocaleString()}
                          </span>
                        </div>
                        <div className="text-right mt-1">
                          <span className="text-gray-500 text-xs">
                            ※サイズ・カラーにより異なります
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* シンプルな確認メッセージ */}
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                <div className="flex items-start space-x-3">
                  <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <SparklesIcon className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <p className="font-bold text-blue-800 mb-1">準備完了！</p>
                    <p className="text-blue-700 text-sm leading-relaxed">
                      アイテムを作成して{selectedItem.supplier === 'printful' ? 'Printful' : 'SUZURI'}で購入できるようになります。
                    </p>
                  </div>
                </div>
              </div>

              {/* スマホサイズの時のみ表示される作成ボタン */}
              <div className="block sm:hidden">
                <button
                  onClick={handleCreate}
                  disabled={!selectedItem || isCreating}
                  className="w-full px-6 py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white font-bold rounded-xl hover:from-purple-700 hover:to-pink-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-md hover:shadow-lg flex items-center justify-center space-x-2"
                >
                  {isCreating ? (
                    <>
                      <ArrowPathIcon className="w-5 h-5 animate-spin" />
                      <span>作成中...</span>
                    </>
                  ) : (
                    <>
                      <SparklesIcon className="w-5 h-5" />
                      <span>アイテムを作成</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          )}
          {step === 'result' && (
            <div className="text-center space-y-8">
              {isCreating ? (
                <div className="py-12">
                  <div className="relative w-24 h-24 mx-auto mb-6">
                    {/* 回転するアイコン */}
                    <div className="w-24 h-24 bg-gradient-to-br from-purple-100 to-pink-100 rounded-full flex items-center justify-center shadow-lg animate-bounce">
                      <ArrowPathIcon className="w-12 h-12 text-purple-600 animate-spin" />
                    </div>
                    {/* 周りを回るキラキラ */}
                    <div className="absolute -top-2 -right-2 w-4 h-4 bg-yellow-400 rounded-full animate-ping"></div>
                    <div className="absolute -bottom-2 -left-2 w-3 h-3 bg-pink-400 rounded-full animate-ping animation-delay-300"></div>
                    <div className="absolute top-1/2 -left-4 w-2 h-2 bg-blue-400 rounded-full animate-ping animation-delay-600"></div>
                  </div>
                  
                  <h3 className="text-2xl font-bold text-gray-800 mb-4">
                    ✨ アイテムを作成中...
                  </h3>
                  <p className="text-gray-600 text-lg leading-relaxed max-w-md mx-auto mb-8">
                    素敵な{selectedItem?.displayName}を作成しています。
                  </p>
                  
                  {/* プログレスバー */}
                  <div className="max-w-xs mx-auto">
                    <div className="w-full h-3 bg-gray-200 rounded-full overflow-hidden shadow-inner">
                      <div className="h-full bg-gradient-to-r from-purple-400 via-pink-400 to-yellow-400 rounded-full animate-pulse relative">
                        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent animate-shimmer"></div>
                      </div>
                    </div>
                    <p className="text-sm text-gray-500 mt-2 text-center">
                      作成中...
                    </p>
                  </div>
                  
                  {/* 小さなプレビュー画像 */}
                  <div className="mt-8 flex justify-center">
                    <div className="relative">
                      <img
                        src={image.url}
                        alt="作成中の画像"
                        className="w-20 h-16 object-contain rounded-lg shadow-lg border-2 border-white transform animate-float"
                      />
                      <div className="absolute -top-1 -right-1 w-6 h-6 bg-gradient-to-br from-purple-400 to-pink-400 rounded-full flex items-center justify-center">
                        <SparklesIcon className="w-4 h-4 text-white animate-pulse" />
                      </div>
                    </div>
                    <div className="mx-6 flex items-center">
                      <ArrowPathIcon className="w-6 h-6 text-purple-500 animate-spin" />
                    </div>
                    <div className="w-20 h-16 bg-gradient-to-br from-purple-100 to-pink-100 rounded-lg flex items-center justify-center shadow-lg border-2 border-white">
                      <span className="text-2xl animate-bounce">{selectedItem?.icon}</span>
                    </div>
                  </div>
                </div>
              ) : result ? (
                <div>
                  {result.success ? (
                    <div className="py-8 space-y-8">
                      {/* 購入ステータス表示（Printful/Prodigi・購入済みの場合のみ最上部に表示） */}
                      {(result.product_url === 'Printful' || result.product_url === 'Prodigi') && purchasedOrderStatus?.has_order ? (
                        <div className="max-w-md mx-auto">
                          <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl p-6 border-2 border-green-400">
                            {/* 購入完了ヘッダー */}
                            <div className="flex items-center justify-center mb-4">
                              <CheckCircleIcon className="w-6 h-6 text-green-600 mr-2" />
                              <span className="font-bold text-green-700 text-lg">
                                {purchasedOrderStatus.orders && purchasedOrderStatus.orders.length > 1
                                  ? `この商品は${purchasedOrderStatus.orders.length}回購入済みです！`
                                  : 'この商品は購入済みです！'}
                              </span>
                            </div>
                            
                            {/* 最新の注文詳細（常に表示） */}
                            {(() => {
                              const latestOrder = purchasedOrderStatus.orders?.[0] || purchasedOrderStatus;
                              return (
                                <div className="space-y-3 bg-white rounded-lg p-4 mb-3">
                                  <div className="font-semibold text-gray-700 mb-2">最新の注文</div>
                                  
                                  {/* 注文番号 */}
                                  {latestOrder.order_id && (
                                    <div className="flex items-center justify-between">
                                      <span className="text-gray-600 font-medium">注文番号:</span>
                                      <span className="text-gray-800 font-semibold">{latestOrder.order_id}</span>
                                    </div>
                                  )}
                                  
                                  {/* ステータス */}
                                  {latestOrder.status && (
                                    <div className="flex items-center justify-between">
                                      <span className="text-gray-600 font-medium">ステータス:</span>
                                      <div className="flex items-center">
                                        {latestOrder.status === 'fulfilled' ? (
                                          <CheckCircleIcon className="w-4 h-4 text-green-500 mr-1" />
                                        ) : latestOrder.status === 'inprocess' ? (
                                          <TruckIcon className="w-4 h-4 text-blue-500 mr-1" />
                                        ) : (
                                          <ClockIcon className="w-4 h-4 text-yellow-500 mr-1" />
                                        )}
                                        <span className={`font-semibold ${
                                          latestOrder.status === 'fulfilled' ? 'text-green-600' :
                                          latestOrder.status === 'inprocess' ? 'text-blue-600' :
                                          'text-yellow-600'
                                        }`}>
                                          {latestOrder.status_text || latestOrder.status}
                                        </span>
                                      </div>
                                    </div>
                                  )}
                                  
                                  {/* 注文日 */}
                                  {latestOrder.created_at && (
                                    <div className="flex items-center justify-between">
                                      <span className="text-gray-600 font-medium">注文日:</span>
                                      <div className="flex items-center text-gray-800">
                                        <CalendarDaysIcon className="w-4 h-4 mr-1 text-gray-500" />
                                        {new Date(latestOrder.created_at).toLocaleDateString('ja-JP')}
                                      </div>
                                    </div>
                                  )}
                                  
                                  {/* 数量 */}
                                  {latestOrder.quantity && (
                                    <div className="flex items-center justify-between">
                                      <span className="text-gray-600 font-medium">数量:</span>
                                      <span className="text-gray-800 font-semibold">{latestOrder.quantity}個</span>
                                    </div>
                                  )}
                                  
                                  {/* 配送先 */}
                                  {latestOrder.recipient_info && (
                                    <div className="pt-2 border-t border-gray-200">
                                      <div className="flex items-start">
                                        <MapPinIcon className="w-4 h-4 text-gray-500 mr-2 mt-0.5" />
                                        <div className="text-sm text-gray-700">
                                          <div className="font-medium">{latestOrder.recipient_info.name}</div>
                                          <div className="text-xs">
                                            〒{latestOrder.recipient_info.zip} {latestOrder.recipient_info.state_name || latestOrder.recipient_info.state_code}
                                            {latestOrder.recipient_info.city} {latestOrder.recipient_info.address1}
                                            {latestOrder.recipient_info.address2 && ` ${latestOrder.recipient_info.address2}`}
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  )}
                                  
                                  {/* 発送日 */}
                                  {latestOrder.shipped_at && (
                                    <div className="flex items-center justify-between">
                                      <span className="text-gray-600 font-medium">発送日:</span>
                                      <div className="flex items-center text-gray-800">
                                        <TruckIcon className="w-4 h-4 mr-1 text-gray-500" />
                                        {new Date(latestOrder.shipped_at).toLocaleDateString('ja-JP')}
                                      </div>
                                    </div>
                                  )}
                                  
                                  {/* 配送状況リンクは非表示 */}
                                </div>
                              );
                            })()}
                            
                            {/* 過去の注文（2件目以降、アコーディオン形式） */}
                            {purchasedOrderStatus.orders && purchasedOrderStatus.orders.length > 1 && (
                              <div className="space-y-2">
                                <div className="font-semibold text-gray-700 text-sm mb-1">過去の注文履歴</div>
                                {purchasedOrderStatus.orders.slice(1).map((order: any, index: number) => (
                                  <div key={order.order_id} className="bg-gray-50 rounded-lg">
                                    <button
                                      onClick={() => toggleOrderExpanded(order.order_id)}
                                      className="w-full px-3 py-2 flex items-center justify-between hover:bg-gray-100 transition-colors rounded-lg"
                                    >
                                      <div className="flex items-center space-x-3">
                                        <ShoppingBagIcon className="w-4 h-4 text-gray-500" />
                                        <span className="text-sm font-medium text-gray-700">
                                          {order.order_id}
                                        </span>
                                        <span className="text-sm text-gray-600">
                                          {new Date(order.created_at!).toLocaleDateString('ja-JP')}
                                        </span>
                                        <span className={`text-sm font-medium ${
                                          order.status === 'fulfilled' ? 'text-green-600' :
                                          order.status === 'inprocess' ? 'text-blue-600' :
                                          'text-yellow-600'
                                        }`}>
                                          {order.status_text}
                                        </span>
                                      </div>
                                      {expandedOrders.has(order.order_id) ? (
                                        <ChevronUpIcon className="w-4 h-4 text-gray-500" />
                                      ) : (
                                        <ChevronDownIcon className="w-4 h-4 text-gray-500" />
                                      )}
                                    </button>
                                    
                                    {/* 展開時の詳細情報 */}
                                    {expandedOrders.has(order.order_id) && (
                                      <div className="px-3 pb-3 space-y-2">
                                        <div className="bg-white rounded p-3 space-y-2 text-sm">
                                          {/* 数量 */}
                                          {order.quantity && (
                                            <div className="flex items-center justify-between">
                                              <span className="text-gray-600">数量:</span>
                                              <span className="text-gray-800">{order.quantity}個</span>
                                            </div>
                                          )}
                                          
                                          {/* 配送先 */}
                                          {order.recipient_info && (
                                            <div className="pt-2 border-t border-gray-100">
                                              <div className="flex items-start">
                                                <MapPinIcon className="w-4 h-4 text-gray-400 mr-2 mt-0.5" />
                                                <div className="text-gray-700">
                                                  <div className="font-medium text-xs">{order.recipient_info.name}</div>
                                                  <div className="text-xs">
                                                    〒{order.recipient_info.zip} {order.recipient_info.state_name || order.recipient_info.state_code}
                                                    {order.recipient_info.city} {order.recipient_info.address1}
                                                    {order.recipient_info.address2 && ` ${order.recipient_info.address2}`}
                                                  </div>
                                                </div>
                                              </div>
                                            </div>
                                          )}
                                          
                                          {/* 配送状況リンクは非表示 */}
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>
                      ) : isFreshCreation ? (
                        /* 今作成した直後だけ成功アニメーション（緑チェック）を表示 */
                        <div className="relative">
                          <div className="w-24 h-24 bg-gradient-to-br from-green-100 to-emerald-100 rounded-full flex items-center justify-center mx-auto mb-6 shadow-xl animate-bounce">
                            <CheckIcon className="w-12 h-12 text-green-600" />
                          </div>
                        </div>
                      ) : null}
                      
                      <div>
                        {isFreshCreation ? (
                          <>
                            <h3 className="text-3xl font-bold text-gray-800 mb-4">
                              🎉 アイテム作成完了！
                            </h3>
                            <p className="text-gray-600 text-lg">
                              素敵な<span className="font-bold text-gray-800">{selectedItem?.displayName || (result as any)?.product_title || (result as any)?.productTitle || (result as any)?.item_name || 'アイテム'}</span>が正常に作成されました
                            </p>
                          </>
                        ) : (
                          /* 履歴閲覧: 演出なしで作成したアイテム名を表示 */
                          <h3 className="text-2xl font-bold text-gray-800 mb-2">
                            {selectedItem?.displayName || (result as any)?.product_title || (result as any)?.productTitle || 'アイテム'}
                          </h3>
                        )}
                        {/* 何を作ったか（商品名＋バリエーション）を明示 */}
                        {(() => {
                          const goodsName = selectedItem?.displayName || (result as any)?.product_title || (result as any)?.productTitle || '';
                          const variant = (result as any)?.variant_label || (selectedItem as any)?.variantLabel
                            || (result as any)?.size || '';
                          // 履歴では見出しに商品名を出すので、バリエーションがある時だけ補足表示
                          const showBadge = isFreshCreation ? (goodsName || variant) : variant;
                          return showBadge ? (
                            <div className="mt-3 inline-flex items-center gap-2 px-4 py-1.5 bg-gray-100 rounded-full text-sm">
                              {isFreshCreation && <span className="font-semibold text-gray-800">{goodsName || 'アイテム'}</span>}
                              {variant && <span className="text-gray-500">{isFreshCreation ? '/ ' : ''}{variant}</span>}
                            </div>
                          ) : null;
                        })()}
                      </div>

                      {/* 作成されたグッズの画像 */}
                      {(result.sampleImageUrl || result.sample_image_url) && (
                        <div className="flex justify-center">
                          <div className="relative group">
                            <img
                              src={result.sampleImageUrl || result.sample_image_url}
                              alt="作成されたグッズ"
                              className="max-w-[250px] h-auto rounded-2xl shadow-2xl border-4 border-white transform hover:scale-105 transition-all duration-500 group-hover:shadow-3xl"
                            />
                            <div className="absolute -top-3 -right-3 bg-gradient-to-r from-green-400 to-emerald-400 text-white text-sm px-3 py-1 rounded-full font-bold shadow-lg animate-pulse">
                              完成品
                            </div>
                            {/* 魔法のエフェクト */}
                            <div className="absolute inset-0 rounded-2xl bg-gradient-to-r from-yellow-400/20 to-pink-400/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                            <div className="absolute -top-2 -left-2 w-4 h-4 bg-yellow-400 rounded-full animate-ping opacity-75"></div>
                            <div className="absolute -bottom-2 -right-2 w-3 h-3 bg-pink-400 rounded-full animate-ping opacity-75 animation-delay-300"></div>
                          </div>
                        </div>
                      )}
                      
                      {/* アクションボタン */}
                      <div className="space-y-4 max-w-md mx-auto">
                        {(result.productUrl || result.product_url) ? (
                          result.product_url === 'Printful' ? (
                            // Printful商品の場合はボタンで購入モーダルを開く
                            <button
                              onClick={() => {
                                let printfulItemToPass: ItemOption | null = null;
                                
                                // 履歴から開いた場合は、resultのモックアップURLを優先
                                if (selectedItem && existingProductData && result) {
                                  // selectedItemをベースに、resultのモックアップURLを上書き
                                  const printfulItemWithMockup = {
                                    ...selectedItem,
                                    sample_image_url: result.sample_image_url || result.sampleImageUrl || selectedItem.sample_image_url
                                  };
                                  console.log('📦 履歴からPrintful商品を復元（selectedItem + result）:', {
                                    selectedItem_sample_image_url: selectedItem.sample_image_url,
                                    result_sample_image_url: result.sample_image_url,
                                    final_sample_image_url: printfulItemWithMockup.sample_image_url
                                  });
                                  printfulItemToPass = printfulItemWithMockup;
                                  setSelectedPrintfulItem(printfulItemWithMockup);
                                } else if (selectedItem) {
                                  // 通常の商品選択の場合
                                  // resultが存在する場合は、resultのモックアップURLを使用
                                  if (result && (result.sample_image_url || result.sampleImageUrl)) {
                                    const printfulItemWithMockup = {
                                      ...selectedItem,
                                      sample_image_url: result.sample_image_url || result.sampleImageUrl || selectedItem.sample_image_url
                                    };
                                    console.log('🎯 グッズ作成直後：resultのモックアップURLを使用:', {
                                      selectedItem_sample_image_url: selectedItem.sample_image_url,
                                      result_sample_image_url: result.sample_image_url || result.sampleImageUrl,
                                      final_sample_image_url: printfulItemWithMockup.sample_image_url
                                    });
                                    printfulItemToPass = printfulItemWithMockup;
                                    setSelectedPrintfulItem(printfulItemWithMockup);
                                  } else {
                                    printfulItemToPass = selectedItem;
                                    setSelectedPrintfulItem(selectedItem);
                                  }
                                } else if (result) {
                                  // resultから最小限の情報を持つItemOptionを作成
                                  // product_idをcatalogIdとして使用
                                  const catalogId = String(result.product_id || result.productId || '');
                                  
                                  // デバッグログ
                                  console.log('🔍 Printful商品データ復元:', {
                                    catalogId,
                                    result_sample_image_url: result.sample_image_url,
                                    result_sampleImageUrl: result.sampleImageUrl,
                                    result_mockup_url: result.mockup_url,
                                    fullResult: result
                                  });
                                  
                                  const printfulItem: ItemOption = {
                                    id: catalogId,  // catalogIdをIDとして設定
                                    name: result.product_title || result.productTitle || 'Printful商品',
                                    displayName: result.product_title || result.productTitle || 'Printful商品',
                                    icon: '🎁',
                                    basePrice: 2500,
                                    finalPrice: 2500,
                                    description: 'Printful商品',
                                    gradient: 'from-gray-600 to-gray-500',
                                    suzuriItemId: 0,
                                    isPublic: true,
                                    displayOrder: 0,
                                    descriptions: [],
                                    availablePrintPlaces: [],
                                    isMultiPrintable: false,
                                    itemType: 'printful',
                                    supplier: 'printful',
                                    vendorItemId: catalogId,  // catalogIdをvendorItemIdとして設定
                                    // resultから直接モックアップURLを取得（カタログ画像ではなく）
                                    sample_image_url: result.sample_image_url || result.sampleImageUrl || result.mockup_url,
                                    apiConfig: {},
                                  };
                                  
                                  // resultに保存されているモックアップURLを保持
                                  printfulItem._mockupUrlFromResult = result.sample_image_url || result.sampleImageUrl;
                                  
                                  printfulItemToPass = printfulItem;
                                  setSelectedPrintfulItem(printfulItem);
                                }
                                
                                // Printful購入モーダルを開く
                                setShowPrintfulModal(true);
                              }}
                              className="inline-flex items-center justify-center w-full px-8 py-4 bg-gradient-to-r from-gray-700 via-gray-600 to-gray-500 text-white font-bold rounded-2xl hover:from-gray-800 hover:to-gray-600 transition-all shadow-xl hover:shadow-2xl transform hover:-translate-y-1"
                            >
                              <ShoppingBagIcon className="w-6 h-6 mr-3" />
                              Printfulで作成した商品を購入する
                            </button>
                          ) : result.product_url === 'Prodigi' ? (
                            // Prodigi商品もPrintfulと同じ購入レール（バリエーション→配送先→確認→決済→完了）
                            <button
                              onClick={() => setShowProdigiOrderModal(true)}
                              className="inline-flex items-center justify-center w-full px-8 py-4 bg-gradient-to-r from-indigo-600 via-indigo-500 to-indigo-400 text-white font-bold rounded-2xl hover:from-indigo-700 hover:to-indigo-500 transition-all shadow-xl hover:shadow-2xl transform hover:-translate-y-1"
                            >
                              <ShoppingBagIcon className="w-6 h-6 mr-3" />
                              Prodigiで作成した商品を購入する
                            </button>
                          ) : (
                            // SUZURI商品の場合は外部リンク
                            <>
                              <a
                                href={isPublished ? (result.productUrl || result.product_url) : '#'}
                                target={isPublished ? "_blank" : undefined}
                                rel={isPublished ? "noopener noreferrer" : undefined}
                                className={`inline-flex items-center justify-center w-full px-8 py-4 font-bold rounded-2xl transition-all shadow-xl transform ${
                                  isPublished 
                                    ? 'bg-gradient-to-r from-purple-600 via-purple-500 to-pink-500 text-white hover:from-purple-700 hover:to-pink-600 hover:shadow-2xl hover:-translate-y-1 cursor-pointer'
                                    : 'bg-gray-300 text-gray-500 cursor-not-allowed opacity-60'
                                }`}
                                onClick={(e) => {
                                  if (!isPublished) {
                                    e.preventDefault();
                                    alert('この商品は非公開です。公開してから購入してください。');
                                    return;
                                  }
                                  const url = result.productUrl || result.product_url;
                                  if (!url) {
                                    e.preventDefault();
                                    alert('商品URLが見つかりません。');
                                    return;
                                  }
                                  
                                  // URLの検証
                                  try {
                                    new URL(url);
                                  } catch (error) {
                                    e.preventDefault();
                                    console.error('無効なURL:', url);
                                    alert('無効な商品URLです: ' + url);
                                    return;
                                  }
                                }}
                              >
                                <ShoppingBagIcon className="w-6 h-6 mr-3" />
                                SUZURIで購入する
                              </a>
                              
                              {/* 公開/非公開切り替えボタン */}
                              <button
                                onClick={toggleVisibility}
                                disabled={isTogglingVisibility}
                                className={`w-full px-8 py-3 mt-3 font-semibold rounded-2xl transition-all border-2 ${
                                  isPublished
                                    ? 'bg-white text-red-600 border-red-300 hover:bg-red-50 hover:border-red-400'
                                    : 'bg-white text-green-600 border-green-300 hover:bg-green-50 hover:border-green-400'
                                } ${
                                  isTogglingVisibility ? 'opacity-50 cursor-not-allowed' : ''
                                }`}
                              >
                                {isTogglingVisibility ? (
                                  '処理中...'
                                ) : isPublished ? (
                                  'SUZURIで非公開にする'
                                ) : (
                                  'SUZURIで公開する'
                                )}
                              </button>
                            </>
                          )
                        ) : (
                          <div className="text-center py-4">
                            <p className="text-red-600 mb-4">商品URLが取得できませんでした</p>
                            <button
                              onClick={() => {
                                alert('SUZURI応答の詳細をコンソールで確認してください');
                              }}
                              className="text-sm text-gray-500 underline"
                            >
                              詳細をコンソールで確認
                            </button>
                          </div>
                        )}
                        
                        <button
                          onClick={handleBack}
                          className="w-full mt-3 px-8 py-4 bg-gray-100 text-gray-700 font-semibold rounded-2xl hover:bg-gray-200 transition-all border-2 border-gray-200 hover:border-gray-300"
                        >
                          他のアイテムも作成する
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="py-8 space-y-6">
                      <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg">
                        <ExclamationTriangleIcon className="w-10 h-10 text-red-600" />
                      </div>
                      
                      <div>
                        <h3 className="text-2xl font-bold text-gray-800 mb-4">
                          エラーが発生しました
                        </h3>
                        <div className="bg-red-50 border border-red-200 rounded-2xl p-6 max-w-lg mx-auto">
                          <p className="text-red-700 leading-relaxed">
                            {result.error}
                          </p>
                        </div>
                      </div>
                      
                      <button
                        onClick={handleBack}
                        className="px-8 py-3 bg-gray-100 text-gray-700 font-semibold rounded-xl hover:bg-gray-200 transition-colors"
                      >
                        戻る
                      </button>
                    </div>
                  )}
                </div>
              ) : null}
            </div>
          )}
        </div>

        {/* シンプルなフッター */}
        {step !== 'result' && (
          <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-between items-center">
            {step === 'select' ? (
              <>
                <div className="flex items-center space-x-1 text-gray-500">
                  <span className="text-sm">
                    {[
                      allowedSuppliers.includes('suzuri') && 'AISHA × SUZURI',
                      allowedSuppliers.includes('printful') && 'AISHA × Printful',
                      allowedSuppliers.includes('artisan') && 'AISHA × Artisan',
                    ].filter(Boolean).join(' ｜ ') || 'AISHA'}
                  </span>
                </div>
                <button
                  onClick={handleClose}
                  className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors font-medium rounded-lg hover:bg-gray-100"
                >
                  キャンセル
                </button>
              </>
            ) : (
              <>
                <button
                  onClick={handleBack}
                  className="flex items-center space-x-2 px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors font-medium rounded-lg hover:bg-gray-100"
                >
                  <ArrowLeftIcon className="w-4 h-4" />
                  <span>戻る</span>
                </button>
                <button
                  onClick={handleCreate}
                  disabled={!selectedItem || isCreating}
                  className="px-6 py-2 bg-gradient-to-r from-purple-600 to-pink-600 text-white font-bold rounded-lg hover:from-purple-700 hover:to-pink-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-md hover:shadow-lg"
                >
                  {isCreating ? (
                    <div className="flex items-center space-x-2">
                      <ArrowPathIcon className="w-4 h-4 animate-spin" />
                      <span>作成中...</span>
                    </div>
                  ) : (
                    <div className="flex items-center space-x-2">
                      <SparklesIcon className="w-4 h-4" />
                      <span>アイテムを作成</span>
                    </div>
                  )}
                </button>
              </>
            )}
          </div>
        )}
      </div>
      
      {/* Printful購入モーダル */}
      {showPrintfulModal && selectedPrintfulItem && (
        <PrintfulPurchaseModal
          isOpen={showPrintfulModal}
          onClose={() => {
            setShowPrintfulModal(false);
            setSelectedPrintfulItem(null);
          }}
          userId={currentUser?.id || ''}
          imageUrl={image.url}  // 生成画像のURLを正しく渡す
          catalogId={selectedPrintfulItem.vendorItemId || ''}
          goodsName={selectedPrintfulItem.displayName}
          merchandiseId={result?.merchandise_id || undefined}  // SuzuriMerchandise IDを渡す
          onPurchaseCompleted={handlePrintfulPurchaseCompleted}  // 購入完了時のコールバック
          mockupUrl={(() => {
            const url = selectedPrintfulItem.sample_image_url || undefined;
            console.log('🎯 PrintfulPurchaseModalに渡すmockupUrl:', {
              mockupUrl: url,
              merchandiseId: result?.merchandise_id,
              selectedPrintfulItem_sample_image_url: selectedPrintfulItem.sample_image_url,
              selectedPrintfulItem: selectedPrintfulItem
            });
            return url;
          })()}  // モックアップ画像URLを渡す
        />
      )}
      {/* Prodigi購入モーダル（位置調整エディタ） */}
      {showProdigiModal && selectedProdigiItem && (
        <ProdigiPurchaseModal
          isOpen={showProdigiModal}
          onClose={() => {
            setShowProdigiModal(false);
            setSelectedProdigiItem(null);
          }}
          imageUrl={image.url || ''}
          item={selectedProdigiItem as any}
          userId={currentUser?.id || ''}
          libraryImageId={(image as any).frontend_id || String(image.id || '')}
          carName={carName}
          onCreated={onGoodsCreated}
        />
      )}
      {/* Prodigi購入モーダル（作成済みアイテムを購入。Printfulと同じ購入フロー） */}
      {showProdigiOrderModal && result && (
        <ProdigiOrderModal
          isOpen={showProdigiOrderModal}
          onClose={() => setShowProdigiOrderModal(false)}
          userId={currentUser?.id || ''}
          merchandiseId={(result as any).merchandise_id || (result as any).merchandiseId}
          goodsId={Number((result as any).product_id || (result as any).productId) || undefined}
          sku={(result as any).item_name || (result as any).itemName || ''}
          copies={1}
          imageUrl={(result as any).sample_image_url || (result as any).sampleImageUrl || image.url || ''}
          goodsName={(result as any).product_title || (result as any).productTitle || 'Prodigi商品'}
          onCompleted={onGoodsCreated}
        />
      )}
      {/* Artisan購入モーダル */}
      {showArtisanPurchase && selectedArtisanProduct && currentUser?.id && (
        <ArtisanPurchaseModal
          isOpen={showArtisanPurchase}
          onClose={() => {
            setShowArtisanPurchase(false);
            setSelectedArtisanProduct(null);
          }}
          userId={currentUser?.id || ''}
          imageUrl={image.url || ''}
          product={selectedArtisanProduct}
          onPurchaseCompleted={() => {
            setShowArtisanPurchase(false);
            setSelectedArtisanProduct(null);
            if (onGoodsCreated) onGoodsCreated();
          }}
        />
      )}
    </div>
  );
};